import { EncryptionKey } from '@/api/types/user';
import { JSEncrypt } from 'jsencrypt';

/**
 * 加密工具类
 * 提供RSA公钥加密和AES-GCM消息解密功能
 */
export class CryptoUtils {
  /**
   * 使用WebCrypto API进行RSA-OAEP+SHA256加密（推荐方法）
   * 匹配服务端期望的加密算法：RSA-OAEP + SHA-256
   * @param password 明文密码
   * @param publicKey PEM格式的公钥
   * @returns 加密后的密码（Base64编码）
   */
  static async encryptPassword(password: string, publicKey: string): Promise<string> {
    try {
      return await CryptoUtils.encryptPasswordWithWebCrypto(password, publicKey);
    } catch (error) {
      console.error('RSA-OAEP密码加密失败:', error);
      // 降级到JSEncrypt（PKCS1）
      console.warn('降级使用JSEncrypt（PKCS1）加密');
      return CryptoUtils.encryptPasswordWithJSEncrypt(password, publicKey);
    }
  }

  /**
   * 使用JSEncrypt进行RSA-PKCS1加密（备用方法）
   * @param password 明文密码
   * @param publicKey PEM格式的公钥
   * @returns 加密后的密码（Base64编码）
   */
  static encryptPasswordWithJSEncrypt(password: string, publicKey: string): string {
    try {
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKey);

      const encrypted = encrypt.encrypt(password);
      if (!encrypted) {
        throw new Error('RSA加密失败');
      }

      return encrypted;
    } catch (error) {
      console.error('JSEncrypt RSA密码加密失败:', error);
      throw new Error('JSEncrypt RSA密码加密失败');
    }
  }

  /**
   * 使用JSEncrypt进行RSA加密的异步版本
   * @param password 明文密码
   * @param publicKey PEM格式的公钥
   * @returns 加密后的密码（Base64编码）
   */
  static async encryptPasswordAsync(password: string, publicKey: string): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const result = CryptoUtils.encryptPassword(password, publicKey);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 使用WebCrypto API进行RSA加密
   * @param password 明文密码
   * @param publicKey PEM格式的公钥
   * @returns 加密后的密码（Base64编码）
   */
  static async encryptPasswordWithWebCrypto(password: string, publicKey: string): Promise<string> {
    try {
      // 解析PEM格式的公钥
      const pemHeader = '-----BEGIN PUBLIC KEY-----';
      const pemFooter = '-----END PUBLIC KEY-----';

      const pemContents = publicKey
        .replace(pemHeader, '')
        .replace(pemFooter, '')
        .replace(/\s+/g, '');

      // 将Base64转换为ArrayBuffer
      const binaryDer = Uint8Array.from(atob(pemContents), c => c.charCodeAt(0));

      // 导入公钥
      const cryptoKey = await window.crypto.subtle.importKey(
        'spki',
        binaryDer,
        {
          name: 'RSA-OAEP',
          hash: 'SHA-256'
        },
        false,
        ['encrypt']
      );

      // 加密密码
      const encoder = new TextEncoder();
      const data = encoder.encode(password);

      const encrypted = await window.crypto.subtle.encrypt(
        {
          name: 'RSA-OAEP'
        },
        cryptoKey,
        data
      );

      // 转换为Base64
      const encryptedArray = new Uint8Array(encrypted);
      const encryptedBase64 = btoa(String.fromCharCode(...encryptedArray));

      return encryptedBase64;
    } catch (error) {
      console.error('WebCrypto RSA加密失败:', error);
      throw new Error('WebCrypto RSA加密失败');
    }
  }

  /**
   * 加密消息内容
   * @param plaintext 明文消息内容
   * @param encryptionKey 加密密钥（十六进制字符串）
   * @returns 加密后的数据 {content, iv, authTag}
   */
  static async encryptMessage(
    plaintext: string,
    encryptionKey: EncryptionKey
  ): Promise<{ content: string, iv: string, authTag: string, key_id: string }> {
    try {
      // 将十六进制密钥转换为 Uint8Array
      const keyBytes = this.hexToBytes(encryptionKey.key_value);

      // 生成随机 IV（96位 = 12字节）
      const iv = crypto.getRandomValues(new Uint8Array(12));

      // 导入密钥 - 将算法名称标准化为 Web Crypto API 格式
      const algorithmName = this.normalizeAlgorithmName(encryptionKey.algorithm);
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBytes as BufferSource,
        { name: algorithmName },
        false,
        ['encrypt']
      );

      // 编码明文
      const encoder = new TextEncoder();
      const plaintextBytes = encoder.encode(plaintext);

      // 加密
      const encryptedBuffer = await crypto.subtle.encrypt(
        {
          name: algorithmName,
          iv: iv,
          tagLength: 128 // 128位认证标签
        },
        cryptoKey,
        plaintextBytes
      );

      // Web Crypto API 返回的 encryptedBuffer 已经包含了密文+认证标签
      // 为了与解密方法兼容，我们需要将完整的 encryptedBuffer 作为 content
      const encryptedArray = new Uint8Array(encryptedBuffer);
      const authTag = encryptedArray.slice(-16); // 最后16字节是认证标签（用于返回，但解密时不需要单独使用）

      return {
        content: this.bytesToHex(encryptedArray), // 完整的加密数据（密文+认证标签）
        iv: this.bytesToHex(iv),
        key_id: encryptionKey.id,
        authTag: this.bytesToHex(authTag) // 单独返回认证标签（供参考或其他用途）
      };
    } catch (error) {
      console.error('消息加密失败:', error);
      throw new Error('消息加密失败');
    }
  }

  /**
   * 解密消息内容
   * @param encryptedContent 加密的消息内容（十六进制字符串）
   * @param iv 初始化向量（十六进制字符串）
   * @param authTag 认证标签（十六进制字符串）
   * @param encryptionKey 解密密钥（十六进制字符串）
   * @returns 解密后的消息内容
   */
  static async decryptMessage(
    encryptedContent: string,
    iv: string,
    _authTag: string,
    encryptionKey: EncryptionKey
  ): Promise<string> {
    try {
      // 将十六进制字符串转换为 Uint8Array
      const keyBytes = this.hexToBytes(encryptionKey.key_value);
      const ivBytes = this.hexToBytes(iv);
      const cipherBytes = this.hexToBytes(encryptedContent);
      // authTag 在 AES-GCM 中会自动处理，不需要单独传递

      // 导入密钥 - 将算法名称标准化为 Web Crypto API 格式
      const algorithmName = this.normalizeAlgorithmName(encryptionKey.algorithm);
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBytes as BufferSource,
        { name: algorithmName },
        false,
        ['decrypt']
      );

      // 使用 Web Crypto API 进行 AES-GCM 解密
      // 注意：Web Crypto API 会自动处理认证标签验证，只需传递密文本身
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: algorithmName,
          iv: ivBytes as BufferSource,
          tagLength: 128 // 128位认证标签长度
        },
        cryptoKey,
        cipherBytes as BufferSource
      );

      // 将解密后的数据转换为字符串
      const decoder = new TextDecoder('utf-8');
      const decryptedText = decoder.decode(decryptedBuffer);

      return decryptedText;
    } catch (error) {
      console.error('消息解密失败:', error);
      return JSON.stringify({
        content: '[解密失败]',
      });
    }
  }

  /**
   * 标准化算法名称为 Web Crypto API 格式
   * @param algorithm 原始算法名称
   * @returns 标准化后的算法名称
   */
  private static normalizeAlgorithmName(algorithm: string): string {
    // 将服务端返回的算法名称转换为 Web Crypto API 标准格式
    switch (algorithm.toLowerCase()) {
      case 'aes-256-gcm':
      case 'aes-gcm':
        return 'AES-GCM';
      case 'aes-256-cbc':
      case 'aes-cbc':
        return 'AES-CBC';
      default:
        // 如果已经是标准格式，直接返回
        return algorithm;
    }
  }

  /**
   * Uint8Array 转十六进制字符串
   * @param bytes 字节数组
   * @returns 十六进制字符串
   */
  private static bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * 十六进制字符串转 Uint8Array
   * @param hex 十六进制字符串
   * @returns Uint8Array
   */
  private static hexToBytes(hex: string): Uint8Array {
    if (!hex || typeof hex !== 'string') {
      throw new Error(`Invalid hex string: ${hex}`);
    }

    if (hex.length % 2 !== 0) {
      throw new Error(`Hex string length must be even: ${hex.length}`);
    }

    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      const hexPair = hex.substr(i, 2);
      const byte = parseInt(hexPair, 16);
      if (isNaN(byte)) {
        throw new Error(`Invalid hex pair at position ${i}: ${hexPair}`);
      }
      bytes[i / 2] = byte;
    }

    return bytes;
  }

  /**
   * 格式化消息时间
   * @param timestamp 时间戳
   * @returns 格式化后的时间字符串
   */
  static formatMessageTime(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (messageDate.getTime() === today.getTime()) {
      // 今天的消息只显示时间
      return date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      // 其他日期显示完整日期时间
      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
}

export default CryptoUtils;
